import type {FenzSafeDocument} from './fenzsafeStore';

/**
 * FenzLock integrity work, kept strictly at the product/application-contract level (Priority 7 of
 * the 2026-09-12 checkpoint instruction) -- what the UI presents about a document's provenance and
 * verification, not the hash-enforcement infrastructure behind it. That infrastructure is real
 * protected/shared territory (see docs/ai/FENZSAFE_CODEX_INTEGRATION_PACKAGE.md §8.4): the reusable
 * primitive already exists (`app/Custody/CustodyIntegrity.php`'s `evidenceHash()`/`eventHash()`),
 * but wiring a real hash requires the storage-FK decision from §8.1 first (a hash is naturally
 * computed at real-storage-accept time). This module defined the *shape* those decisions fill in
 * later -- one place the UI asks "what is this document's verification status," and now that the
 * real pipeline exists (`content_sha256` from `DocumentController::serialize()`, backed by
 * `PdoFenzSafePrivateObjectRegistryStorage::contentSha256Of()`), only this function's body changed
 * -- no caller here or in FenzSafeApp.tsx needed to.
 */

export const DOCUMENT_VERIFICATION_STATUSES = ['not_computed', 'verified', 'mismatch'] as const;
export type DocumentVerificationStatus = (typeof DOCUMENT_VERIFICATION_STATUSES)[number];

export function verificationStatusLabel(status: DocumentVerificationStatus): string {
  switch (status) {
    case 'not_computed':
      return 'Not yet verified — no cryptographic hash computed in this preview';
    case 'verified':
      return 'Verified — hash matches the original';
    case 'mismatch':
      return 'Verification failed — hash does not match';
  }
}

/**
 * A short, decorative glyph paired with each status's own text label (never a color-only signal --
 * see this badge's CSS, which already carries a background/text color too). Reusable UI idiom
 * adapted from Gemini/AG's gem-003 `DataBoundaryBadge` concept (Issue #164 reconciliation ledger,
 * comment 5778454887): icon + short label together, so the distinction survives print/grayscale
 * and doesn't rely on color perception alone. Plain Unicode glyphs, matching this codebase's
 * existing lightweight decorative-mark convention (e.g. FenzSafeApp.tsx's own wordmark `<span
 * aria-hidden="true">F</span>`) rather than adding an icon-library dependency for two badge types.
 */
export function verificationStatusGlyph(status: DocumentVerificationStatus): string {
  switch (status) {
    case 'not_computed':
      return '○';
    case 'verified':
      return '✓';
    case 'mismatch':
      return '✕';
  }
}

/**
 * `contentSha256` is only ever set by the real backend, and only for an 'accepted' document (see
 * `DocumentController::contentSha256()`'s own doc comment) -- so its mere presence already means
 * "the backend computed and persisted this hash at accept time," which is what `verified` means
 * here. There is no local re-hashing of the bytes to check for a `mismatch`: the browser never
 * downloads the plaintext just to render this status, so `mismatch` remains reachable only for a
 * future integrity-check feature, not this one. A document with no real hash yet (still in the
 * synthetic preview store, or not yet accepted) stays honestly `not_computed`.
 */
export function verificationStatusOf(document: FenzSafeDocument): DocumentVerificationStatus {
  return document.contentSha256 ? 'verified' : 'not_computed';
}

/**
 * Original-vs-derivative distinction: an uploaded file is the artifact itself; a phone/message
 * export is necessarily a *capture* of underlying evidence (a screenshot, an exported thread),
 * not the original artifact -- a real distinction an attorney needs to know when relying on it,
 * independent of whether a hash has been computed. Mirrors `FenzSafeDocument.source` exactly
 * rather than inventing a parallel vocabulary; this function exists so "what does this source
 * value mean for provenance" has one real, tested answer instead of being inferred ad hoc at each
 * call site.
 */
export type DocumentProvenanceKind = 'original' | 'derivative';

export function provenanceKindOf(document: Pick<FenzSafeDocument, 'source'>): DocumentProvenanceKind {
  return document.source === 'phone_import' ? 'derivative' : 'original';
}

export function provenanceLabel(kind: DocumentProvenanceKind): string {
  return kind === 'original' ? 'Original file' : 'Derivative capture — from a phone/device export';
}

/** Same icon+label idiom as verificationStatusGlyph() above, applied to provenance. */
export function provenanceGlyph(kind: DocumentProvenanceKind): string {
  return kind === 'original' ? '◆' : '↳';
}
